import React, { useMemo } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Tooltip,
  makeStyles,
  Theme,
  CircularProgress,
} from '@bb-ui/react-library';
import { IconButton } from '@bb-ui/react-library/dist/components/IconButton';
import { Clear } from '@bb-ui/icons/dist/small/Clear';
import { Redo } from '@bb-ui/icons/dist/small/Redo';
import { useTranslation } from 'react-i18next';
import { TableSkeleton, useSkeletonState } from '../../shared';
import { SiteInCohort } from 'App.types';
import { AlertError, ContentLink } from '@bb-ui/icons/dist/small';
import CancelSiteExecutionControls from './CancelSiteExecutionControls';
import { PENDING_STATUSES, ACTIVE_STATUSES, Status } from '@lct-jobs/core';
import { isCohortInProgress, isCohortCompleted, formatGmtInZone } from '../../utils';
import { SiteSortConfig, SiteSortKey } from '../../hooks/useCohortDetailLogic';

interface SitesTableProps {
  sites: SiteInCohort[];
  onRemoveSite: (siteId: string) => void;
  onRetrySite?: (siteId: string, anchorEl: HTMLElement) => void;
  cohortStatus: string;
  visibleSitesCount: number;
  cohortId: string;
  downtimeDate?: string | null;
  loadingRemoveSite: boolean;
  isLoadingSites?: boolean;
  upgradeWorkflowStatus?: string;
  retryingSiteIds?: string[];
  enableNotifications?: boolean;
  timeZone?: string;
  sortConfig?: SiteSortConfig | null;
  onSort?: (key: SiteSortKey) => void;
  hasActiveFilters?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  tableWrapper: {
    height: 340,
    overflowY: 'auto',
    marginBottom: theme.spacing(2),
  },
  cellText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.dark,
    },
  },
  badgeCell: {
    width: 48,
    textAlign: 'center',
  },
  removingRow: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  removeButton: {
    transition: 'all 0.2s ease',
    '&:disabled': {
      opacity: 0.6,
    },
  },
  loadingOverlay: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
      pointerEvents: 'none',
      zIndex: 1,
    },
  },
  alertSpacing: {
    marginLeft: theme.spacing(2),
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  retryButton: {
    color: theme.palette.primary.main,
    transition: 'all 0.2s ease',
    '&:hover': {
      color: theme.palette.primary.dark,
    },
    '&:disabled': {
      opacity: 0.6,
    },
  },
}));

const SitesTable: React.FC<SitesTableProps> = ({
  sites,
  onRemoveSite,
  onRetrySite,
  cohortStatus,
  visibleSitesCount,
  downtimeDate,
  loadingRemoveSite,
  isLoadingSites = false,
  upgradeWorkflowStatus,
  retryingSiteIds = [],
  enableNotifications = true,
  timeZone,
  sortConfig = null,
  onSort,
  hasActiveFilters = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const renderSortableHeader = (key: SiteSortKey, label: string) => {
    if (!onSort) return label;
    const isActive = sortConfig?.key === key;
    return (
      <TableSortLabel
        active={isActive}
        direction={isActive ? sortConfig?.direction : 'asc'}
        onClick={() => onSort(key)}
      >
        {label}
      </TableSortLabel>
    );
  };

  const { showSkeleton, showLoadingOverlay } = useSkeletonState(isLoadingSites, sites.length > 0);

  const isDowntimeDateReached = useMemo(() => {
    if (!downtimeDate) return false;
    const downtime = new Date(downtimeDate);
    const now = new Date();
    return now >= downtime;
  }, [downtimeDate]);

  const isRemovalDisabledByLock =
    isCohortInProgress(cohortStatus) || isCohortCompleted(cohortStatus);
  const isRemovalDisabledByCount = visibleSitesCount <= 1;

  const showUpgradeStatusColumn = upgradeWorkflowStatus === 'SUCCEEDED';
  const totalColumns = showUpgradeStatusColumn ? 5 : 4;

  const getTooltipMessage = (upgradeJobStatus?: string) => {
    if (upgradeJobStatus === 'PENDING') {
      return 'Cancel this sites upgrade and remove it from the cohort.';
    }
    if (isRemovalDisabledByLock) {
      return 'Sites cannot be removed once the cohort upgrade is in progress or completed. Please contact Platform Team for assistance.';
    }
    if (isRemovalDisabledByCount) {
      return 'At least one site must remain in the cohort.';
    }
    if (loadingRemoveSite) {
      return 'Please wait while the previous operation completes.';
    }
    return 'Remove site from cohort';
  };

  const renderRemoveButton = (site: SiteInCohort) => {
    const isDisabled =
      isRemovalDisabledByLock || isRemovalDisabledByCount || loadingRemoveSite || isLoadingSites;
    const tooltipMessage = getTooltipMessage(site.upgrade_job_status);

    const isRemovableException =
      site.site_status === 'DECLINED' ||
      site.site_status === 'FAILED' ||
      site.upgrade_job_status === 'ROLLBACK_COMPLETE' ||
      site.upgrade_job_status === 'CANCELED' ||
      site.upgrade_job_status === 'PENDING';

    const buttonIcon = loadingRemoveSite ? (
      <CircularProgress size={16} id={`remove-${site.captain_id}-loading`} label="loading" />
    ) : (
      <Clear />
    );

    if (isDisabled && tooltipMessage && !isRemovableException) {
      return (
        <Tooltip title={tooltipMessage}>
          <span>
            <IconButton
              aria-label="Remove site"
              size="small"
              disabled
              className={classes.removeButton}
            >
              {buttonIcon}
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    const showTooltip = site.upgrade_job_status === 'PENDING';

    return (
      <Tooltip title={showTooltip ? tooltipMessage : ''}>
        <IconButton
          aria-label="Remove site"
          size="small"
          onClick={() => onRemoveSite(site.captain_id)}
          disabled={loadingRemoveSite || isLoadingSites}
          className={classes.removeButton}
        >
          {buttonIcon}
        </IconButton>
      </Tooltip>
    );
  };

  const renderDeclinedDowntimeAlert = (site: SiteInCohort) => {
    const newSuggestedDowntimeStart = site.notifications?.start_suggested_date;
    const newSuggestedDowntimeEnd = site.notifications?.end_suggested_date;

    if (!newSuggestedDowntimeStart || !newSuggestedDowntimeEnd) {
      return null;
    }

    const startLabel = formatGmtInZone(newSuggestedDowntimeStart, timeZone);
    const endLabel = formatGmtInZone(newSuggestedDowntimeEnd, timeZone);
    const isSameInstant = newSuggestedDowntimeStart === newSuggestedDowntimeEnd;
    const tooltipContent = (
      <div>
        <div>
          {isSameInstant
            ? 'The cohort downtime has been declined by the client. New suggested downtime date and time:'
            : 'The cohort downtime window has been declined by the client. New suggested downtime window:'}
        </div>
        <div className={classes.alertSpacing}>
          {isSameInstant ? (
            <div>{startLabel}</div>
          ) : (
            <>
              <div>Start: {startLabel}</div>
              <div>End: {endLabel}</div>
            </>
          )}
        </div>
      </div>
    );

    return (
      <Tooltip title={tooltipContent} arrow>
        <AlertError />
      </Tooltip>
    );
  };

  const isValidStatus = (status: string | undefined): status is Status => {
    if (!status) return false;
    return Object.values(Status).includes(status as Status);
  };

  const renderRetryButton = (site: SiteInCohort) => {
    const isCurrentlyRetrying = retryingSiteIds.includes(site.captain_id);
    const isDisabled = isCurrentlyRetrying || loadingRemoveSite || isLoadingSites;

    return (
      <Tooltip title={isCurrentlyRetrying ? 'Retrying site execution...' : 'Retry site execution'}>
        <IconButton
          aria-label="Retry site execution"
          size="small"
          onClick={(e) => onRetrySite?.(site.captain_id, e.currentTarget)}
          disabled={isDisabled}
          className={classes.retryButton}
        >
          {isCurrentlyRetrying ? (
            <CircularProgress size={16} id={`retry-${site.captain_id}-loading`} label="" />
          ) : (
            <Redo />
          )}
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <TableContainer
      className={`${classes.tableWrapper} ${showLoadingOverlay ? classes.loadingOverlay : ''}`}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              className={classes.cellText}
              sortDirection={sortConfig?.key === 'captain_id' ? sortConfig.direction : false}
            >
              {renderSortableHeader('captain_id', t('cohorts.captainId'))}
            </TableCell>
            <TableCell
              className={classes.cellText}
              sortDirection={sortConfig?.key === 'learn_url' ? sortConfig.direction : false}
            >
              {renderSortableHeader('learn_url', t('cohorts.learnUrl'))}
            </TableCell>
            <TableCell
              className={classes.cellText}
              sortDirection={sortConfig?.key === 'status' ? sortConfig.direction : false}
            >
              {renderSortableHeader('status', t('cohorts.siteStatus'))}
            </TableCell>

            {showUpgradeStatusColumn && (
              <TableCell className={classes.cellText}>Site Details</TableCell>
            )}
            <TableCell className={classes.badgeCell}>{t('cohorts.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(() => {
            if (showSkeleton) {
              return (
                <TableSkeleton
                  rows={3}
                  columns={totalColumns}
                  columnWidths={
                    showUpgradeStatusColumn
                      ? ['wide', 'wide', 'medium', 'medium', 'narrow']
                      : ['wide', 'wide', 'medium', 'narrow']
                  }
                />
              );
            }

            if (sites.length === 0) {
              return (
                <TableRow>
                  <TableCell
                    colSpan={totalColumns}
                    style={{ textAlign: 'center', padding: '32px', color: '#666' }}
                  >
                    {hasActiveFilters ? t('cohorts.noMatchingSites') : t('cohorts.noSitesInCohort')}
                  </TableCell>
                </TableRow>
              );
            }

            return sites.map((site) => {
              const isProcessing = loadingRemoveSite;

              const defaultStatus = enableNotifications ? 'PLANNED' : 'READY';

              const displayStatus = site.upgrade_job_status || defaultStatus;
              const siteStatus = site.site_status || defaultStatus;

              const jobExecutionsUrl = `${window.location.origin}/tenants/${site.tenant_id}/executions`;
              const isCurrentlyRetryingThisSite = retryingSiteIds.includes(site.captain_id);
              const showRetryButton =
                (displayStatus === 'ROLLBACK_COMPLETE' || isCurrentlyRetryingThisSite) &&
                onRetrySite &&
                !isDowntimeDateReached;
              const isCancellable =
                isValidStatus(site.upgrade_job_status) &&
                site.upgrade_job_status !== 'PENDING' &&
                ((PENDING_STATUSES as readonly Status[]).includes(site.upgrade_job_status) ||
                  (ACTIVE_STATUSES as readonly Status[]).includes(site.upgrade_job_status));

              return (
                <TableRow key={site.captain_id} className={isProcessing ? classes.removingRow : ''}>
                  <TableCell className={classes.cellText}>{site.captain_id}</TableCell>
                  <TableCell className={classes.cellText}>
                    {site.learn_url ? (
                      <a
                        href={
                          /^https?:\/\//i.test(site.learn_url)
                            ? site.learn_url
                            : `https://${site.learn_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.statusLink}
                      >
                        {site.learn_url}
                      </a>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={classes.statusCell}>
                      {site.upgrade_job_status_reason ? (
                        <Tooltip title={site.upgrade_job_status_reason}>
                          <span>{displayStatus}</span>
                        </Tooltip>
                      ) : (
                        displayStatus
                      )}
                      {showRetryButton && renderRetryButton(site)}
                      {siteStatus === 'DECLINED' ? renderDeclinedDowntimeAlert(site) : null}
                    </div>
                  </TableCell>

                  {showUpgradeStatusColumn && (
                    <TableCell className={classes.cellText}>
                      <a
                        href={jobExecutionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.statusLink}
                        title="View Upgrade Status in Job Executions"
                      >
                        Details
                        <ContentLink style={{ fontSize: 14 }} />
                      </a>
                    </TableCell>
                  )}
                  <TableCell className={classes.badgeCell}>
                    {isCancellable ? (
                      <CancelSiteExecutionControls
                        tenantId={site.tenant_id!}
                        isLoading={isProcessing}
                        isCancellable={isCancellable}
                      />
                    ) : (
                      renderRemoveButton(site)
                    )}
                  </TableCell>
                </TableRow>
              );
            });
          })()}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SitesTable;
